import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { get } from "@/services/api";
import { Loader2, Award, Calendar, User, ArrowRight, GraduationCap, Shield, Filter, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useState, useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import NavBar from "../-components/NavBar";
import Footer from "../-components/Footer";
import Pagination from "@/components/general-components/Pagination";
import Select from "@/components/general-components/Select";
import { Input } from "@/components/ui/input";
import { useDebounce } from "@/hooks/useDebounce";
import { decodeBase64Variables } from "@/utils/decodeBase64Variables";

export const Route = createFileRoute("/_index/certificados/")({
  component: CertificadosPublicos,
});

// Interface para o certificado
interface ICertificate {
  id: number;
  key: string;
  courseId: number;
  traineeId: number;
  classId: number;
  companyId: number;
  expirationDate: string;
  fabricJsonFront: string;
  fabricJsonBack?: string;
  variableToReplace: any;
  pdfUrl?: string;
  createdAt: string;
  updatedAt: string;
  inactiveAt?: string;
}

// Interface para a resposta da API
interface ICertificateResponse {
  total: number;
  rows: ICertificate[];
}

// Interface para curso
interface ICourse {
  id: number;
  name: string;
  icon?: string;
  color?: string;
  active: boolean;
}

// Tipo para compatibilidade com Select
type CourseOption = {
  id: number | string;
  name: string;
  [key: string]: string | number;
}

// Tipo para itens do carrinho
type CartItem = {
  id: number;
  name: string;
  quantity: number;
  imageUrl?: string;
};

function CertificadosPublicos() {
  const navigate = useNavigate();
  const [cart, setCart] = useState<CartItem[]>([]);
  const [page, setPage] = useState(0);
  const [selectedCourseId, setSelectedCourseId] = useState<string>("all");
  const [searchName, setSearchName] = useState<string>("");
  const debouncedSearchName = useDebounce(searchName, 500);
  const limit = 12;

  const handleWhatsApp = (message?: string) => {
    const phone = "5581989479259";
    const defaultMessage = message || "Olá, gostaria de mais informações.";
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(defaultMessage)}`;
    window.open(url, "_blank");
  };

  // Buscar cursos disponíveis
  const { data: courses = [] } = useQuery<ICourse[]>({
    queryKey: ["courses-list"],
    queryFn: async () => {
      const params = [
        { key: 'active', value: true },
        { key: 'limit', value: 100 },
        { key: 'companyId', value: 1 },
      ];
      const response = await get<ICourse[]>('courses', 'list', params);
      return response || [];
    }
  });

  const { 
    data: certificatesData, 
    isLoading,
    isError,
    error
  } = useQuery<ICertificateResponse | undefined>({
    queryKey: [`certificates-list-${page}-${selectedCourseId}-${debouncedSearchName}`],
    queryFn: async () => {
      const params = [
        { key: 'active', value: 'true' },
        { key: 'order-createdAt', value: 'desc' },
        { key: 'page', value: page.toString() },
        { key: 'limit', value: limit.toString() },
        { key: 'show', value: ['trainee'] },
        { key: 'showOnWebsiteConsent', value: true },
      ];
      
      // Adicionar filtro de curso se selecionado (exceto quando for "all")
      if (selectedCourseId && selectedCourseId !== 'all') {
        params.push({ key: 'courseId', value: selectedCourseId });
      }
      
      // Adicionar filtro de nome se houver texto de busca
      if (debouncedSearchName) {
        params.push({ key: 'like-trainee.name', value: debouncedSearchName });
      }
      
      const response = await get('trainee-certificate', '', params);
      return response as ICertificateResponse;
    },
    retry: 1
  });

  // Resetar página ao mudar o filtro
  const handleCourseChange = (_name: string, value: string | string[]) => {
    setSelectedCourseId(typeof value === 'string' ? value : 'all');
    setPage(0);
  };

  // Resetar página quando o texto de busca mudar
  useEffect(() => {
    setPage(0);
  }, [debouncedSearchName]);

  const formatDate = (date: string | Date | undefined) => {
    if (!date) return "—";
    
    try {
      // Se já está no formato dd/MM/yyyy, retorna como está
      if (typeof date === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
        return date;
      }
      
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      
      // Verifica se a data é válida
      if (isNaN(dateObj.getTime())) {
        return "—";
      }
      
      return format(dateObj, "dd/MM/yyyy", { locale: ptBR });
    } catch (error) {
      console.error("Erro ao formatar data:", date, error);
      return "—";
    }
  };

  if (isLoading) {
    return (
      <>
        <NavBar cart={cart} setCart={setCart} handleWhatsApp={handleWhatsApp} />
        <div className="min-h-screen flex items-center justify-center bg-background/50 pt-20">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin mx-auto text-primary-light mb-4" />
            <p className="text-gray-600">Carregando certificados...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (isError) {
    return (
      <>
        <NavBar cart={cart} setCart={setCart} handleWhatsApp={handleWhatsApp} />
        <div className="min-h-screen flex items-center justify-center bg-white pt-20">
          <div className="bg-white rounded-lg border border-gray-200 max-w-md w-full mx-4 p-6">
            <div className="text-center">
              <p className="text-gray-600">
                Erro ao carregar certificados. Tente novamente mais tarde.
              </p>
              {error && (
                <p className="text-sm text-red-500 mt-2">
                  {error instanceof Error ? error.message : "Erro desconhecido"}
                </p>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const certificates = certificatesData?.rows || [];

  return (
    <>
      <NavBar cart={cart} setCart={setCart} handleWhatsApp={handleWhatsApp} />
      <div className="min-h-screen bg-background pt-24 pb-12 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header Simples */}
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-2 bg-green-100 dark:bg-green-950/30 rounded-lg">
                <Shield className="w-6 h-6 text-green-600 dark:text-green-500" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">
                  Profissionais Certificados
                </h1>
                <p className="text-sm text-muted-foreground">
                  Encontre profissionais qualificados pela WorkSafe
                </p>
              </div>
            </div>

            {/* Filtros */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Filter className="w-4 h-4" />
                  <span>Filtros:</span>
                </div>

                {/* Botão Limpar Todos os Filtros */}
                {(selectedCourseId !== 'all' || searchName) && (
                  <button
                    onClick={() => {
                      handleCourseChange('courseFilter', 'all');
                      setSearchName('');
                    }}
                    className="text-sm text-muted-foreground hover:text-foreground underline cursor-pointer"
                  >
                    Limpar todos os filtros
                  </button>
                )}
              </div>
              
              <div className="flex flex-col lg:flex-row gap-4">
                {/* Filtro de Curso */}
                <div className="min-w-[250px]">
                  <Select
                    name="courseFilter"
                    options={[{ id: 'all', name: 'Todos os cursos' } as CourseOption, ...courses.map(c => ({ id: c.id, name: c.name } as CourseOption))]}
                    state={selectedCourseId}
                    label="name"
                    value="id"
                    placeholder="Selecione um curso"
                    onChange={handleCourseChange}
                    disabled={false}
                  />
                </div>

                {/* Busca por Nome */}
                <div className="relative min-w-[250px]">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Buscar por nome do aluno..."
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                    className="pl-10 pr-3 py-2 bg-background border-input"
                  />
                </div>
              </div>
            </div>

            {/* Contador de resultados */}
            {certificatesData?.total !== undefined && (
              <div className="mt-4 text-sm text-muted-foreground">
                {(selectedCourseId && selectedCourseId !== 'all') || searchName ? (
                  <span>
                    {certificatesData.total} {certificatesData.total === 1 ? 'resultado encontrado' : 'resultados encontrados'}
                    {selectedCourseId && selectedCourseId !== 'all' && ' para o curso selecionado'}
                    {searchName && ` com nome contendo "${searchName}"`}
                  </span>
                ) : (
                  <span>
                    Total de {certificatesData.total} {certificatesData.total === 1 ? 'certificado emitido' : 'certificados emitidos'}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Lista de Certificados */}
          {certificates.length === 0 ? (
            <div className="bg-card rounded-lg border border-border p-8">
              <div className="text-center">
                <Award className="w-16 h-16 text-muted-foreground/50 mx-auto mb-4" />
                <p className="text-muted-foreground">Nenhum certificado encontrado</p>
              </div>
            </div>
          ) : (
            <>
              <div className="bg-card rounded-lg border border-border overflow-hidden">
                {/* Header da tabela - Desktop */}
                <div className="hidden md:block bg-muted/50 px-6 py-3 border-b border-border">
                  <div className="grid grid-cols-12 gap-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    <div className="col-span-1">Código</div>
                    <div className="col-span-3">Aluno</div>
                    <div className="col-span-3">Curso</div>
                    <div className="col-span-2">Turma</div>
                    <div className="col-span-2">Conclusão</div>
                    <div className="col-span-1"></div>
                  </div>
                </div>

                {/* Lista de itens */}
                <div className="divide-y divide-border">
                  {certificates.map((certificate) => {
                    const variables = decodeBase64Variables(certificate.variableToReplace) || {};

                    return (
                      <div
                        key={certificate.id}
                        className="px-4 md:px-6 py-4 hover:bg-accent cursor-pointer transition-colors duration-150"
                        onClick={() => navigate({ to: `/certificados/${certificate.key}` })}
                      >
                        {/* Desktop View */}
                        <div className="hidden md:grid grid-cols-12 gap-4 items-center">
                          {/* Código */}
                          <div className="col-span-1">
                            <span className="text-xs font-mono text-muted-foreground">
                              #{variables.certificado_codigo?.value?.slice(-6) || certificate.id}
                            </span>
                          </div>

                          {/* Nome do Aluno */}
                          <div className="col-span-3">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                              <p className="text-sm font-medium text-foreground truncate">
                                {variables.aluno_nome?.value || "—"}
                              </p>
                            </div>
                          </div>

                          {/* Curso */}
                          <div className="col-span-3">
                            <div className="flex items-center gap-2">
                              <GraduationCap className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                              <p className="text-sm text-muted-foreground truncate">
                                {variables.curso_nome?.value || "—"}
                              </p>
                            </div>
                          </div>

                          {/* Turma */}
                          <div className="col-span-2">
                            <p className="text-sm text-muted-foreground truncate">
                              {variables.turma_nome?.value || "—"}
                            </p>
                          </div>

                          {/* Data de Conclusão */}
                          <div className="col-span-2">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-muted-foreground" />
                              <p className="text-sm text-muted-foreground">
                                {formatDate(variables.certificado_emissao?.value || certificate.createdAt)}
                              </p>
                            </div>
                          </div>

                          {/* Ação */}
                          <div className="col-span-1 flex justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-primary hover:text-primary hover:bg-primary/10"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate({ to: `/certificados/${certificate.id}` });
                              }}
                            >
                              <ArrowRight className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>

                        {/* Mobile View */}
                        <div className="md:hidden">
                          <div className="flex justify-between items-start mb-2">
                            <span className="text-xs font-mono text-muted-foreground">
                              #{variables.certificado_codigo?.value?.slice(-6) || certificate.id}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {formatDate(variables.certificado_emissao?.value || certificate.createdAt)}
                            </span>
                          </div>
                          <p className="font-medium text-foreground text-sm mb-1">
                            {variables.aluno_nome?.value || "—"}
                          </p>
                          <p className="text-sm text-muted-foreground mb-1">
                            {variables.curso_nome?.value || "—"}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Turma: {variables.turma_nome?.value || "—"}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Paginação */}
              {certificatesData?.total && certificatesData.total > 0 && (
                <div className="mt-8 w-full">
                  <div className="flex flex-col items-center gap-2">
                    <Pagination
                      totalItems={certificatesData.total}
                      itemsPerPage={limit}
                      currentPage={page}
                      onPageChange={setPage}
                      maxVisiblePages={5}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}

export default CertificadosPublicos;